// ClientsManagement.tsx

import { useEffect, useMemo, useRef, useState } from 'react';
import Pagination from '../Components/Pagination.tsx';
import { Contact, useOrganizations } from '../hooks/useOrganizations.ts';
import Spinner from '../Components/Spinner.tsx';
import Modal from '../Components/Modal.tsx';
import { MdOutlineDeleteOutline } from 'react-icons/md';
import more from '../Images/more-vertical.svg';
import useOnClickOutside from '../hooks/useOnClickOutside.ts';
import user_plus from '../Images/user-plus2.svg';
import { CiEdit } from 'react-icons/ci';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { FiSearch, FiX } from 'react-icons/fi';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import styles from './UsersManagement.module.css';
import { useUsers } from '../hooks/useUsers.ts'; // Import useUsers hook
import { useTheme } from '../hooks/useTheme.ts';

type SortKey = 'name' | 'email' | 'phone' | 'address';
type SortDir = 'asc' | 'desc';

// Utility function to paginate organizations
const getData = function (organizations: Contact[] | undefined, page: number, limit: number) {
  if (organizations === undefined) {
    return [];
  }
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  return organizations.slice(startIndex, endIndex);
};

function getSortValue(org: Contact, key: SortKey): string {
  switch (key) {
    case 'name':
      return (org.name ?? '').toLowerCase();
    case 'email':
      return (org.email ?? '').toLowerCase();
    case 'phone':
      return (org.phone ?? '').toLowerCase();
    case 'address':
      return (org.address ?? '').toLowerCase();
    default:
      return '';
  }
}

function ClientsManagement() {
  // Fetch organizations and users
  const { isLoading, allOrganizations, error } = useOrganizations();
  const { isLoading: isLoadingUsers, allUsers, error: errorUsers } = useUsers(); // Fetch all users
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark';

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const limitOfOrgs = 7;

  // Search + sort state
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Modal state management
  const [showActionsModal, setShowActionsModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Contact | null>(null);
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [openRemoveUserModal, setOpenRemoveUserModal] = useState(false);
  const [openEditOrgModal, setOpenEditOrgModal] = useState(false);
  const [openConfirmDeleteOrgModal, setOpenConfirmDeleteOrgModal] = useState(false);

  // Reference for detecting clicks outside the actions pop-up
  const popUpMenuRef = useRef<HTMLDivElement>(null);

  // Handler to open Add User Modal
  const handleAddUser = (org: Contact) => {
    setSelectedOrg(org);
    setOpenAddUserModal(true);
    setShowActionsModal(false);
  };

  // Handler to open Remove User Modal
  const handleRemoveUser = (org: Contact) => {
    setSelectedOrg(org);
    setOpenRemoveUserModal(true);
    setShowActionsModal(false);
  };

  // Handler to open Edit Organization Modal
  const handleEditOrg = (org: Contact) => {
    setSelectedOrg(org);
    setOpenEditOrgModal(true);
    setShowActionsModal(false);
  };

  // Handler to open Confirm Delete Organization Modal
  const handleDeleteOrg = (org: Contact) => {
    setSelectedOrg(org);
    setOpenConfirmDeleteOrgModal(true);
    setShowActionsModal(false);
  };

  // Toggle Actions Pop-up
  const handleActionsClick = (org: Contact) => {
    setSelectedOrg(org);
    setShowActionsModal(!showActionsModal);
  };

  // Close Actions Pop-up when clicking outside
  const clickOutSideHandler = () => {
    setShowActionsModal(false);
  };

  // @ts-ignore - Ignoring type issue with useOnClickOutside
  useOnClickOutside(popUpMenuRef, clickOutSideHandler);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Filter + sort before paginating, so page 1 always holds the best matches.
  const visibleOrgs = useMemo(() => {
    const list = (allOrganizations ?? []).filter((org: Contact) => org !== undefined);
    const query = search.trim().toLowerCase();

    const filtered = query
      ? list.filter((org: Contact) =>
          [org.name ?? '', org.email ?? '', org.phone ?? '', org.address ?? '']
            .join(' ')
            .toLowerCase()
            .includes(query)
        )
      : list;

    return [...filtered].sort((a: Contact, b: Contact) => {
      const cmp = getSortValue(a, sortKey).localeCompare(getSortValue(b, sortKey), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allOrganizations, search, sortKey, sortDir]);

  // Any change to the result set puts you back on page 1.
  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortKey, sortDir]);

  const sortIcon = (key: SortKey) => {
    if (key !== sortKey) return <FaSort className={styles.sortIcon} />;
    return sortDir === 'asc' ? (
      <FaSortUp className={styles.sortIconActive} />
    ) : (
      <FaSortDown className={styles.sortIconActive} />
    );
  };

  // Render organizations in the table
  const renderOrganizations = () => {
    return getData(visibleOrgs, currentPage, limitOfOrgs).map((org) => {
      return (
        <tr key={org.id}>
          <td>
            <div className={styles.avatar}>{org.name}</div>
          </td>
          <td>{org.email}</td>
          <td>{org.phone}</td>
          <td>{org.address}</td>
          <td className={styles.role_info}>
            <div className={styles.more} onClick={() => handleActionsClick(org)}>
              {isDarkTheme ? (
                <BsThreeDotsVertical
                  size={20}
                  color="#FFFFFF"
                  style={{
                    filter: 'drop-shadow(0px 0px 1px rgba(255, 255, 255, 0.5))',
                    opacity: 0.9,
                  }}
                />
              ) : (
                <img src={more} alt="More Options" />
              )}
              {selectedOrg?.id === org.id && showActionsModal && (
                <div className={styles.popOut} ref={popUpMenuRef}>
                  <ul className={styles.list}>
                    <li>
                      <button className={styles.list_button} onClick={() => handleAddUser(org)}>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                          }}
                        >
                          <img src={user_plus} alt="Add User" className={styles.user_img} />
                          Add User
                        </div>
                      </button>
                    </li>
                    <li>
                      <button className={styles.list_button} onClick={() => handleRemoveUser(org)}>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            color: '#D14D41',
                          }}
                        >
                          <MdOutlineDeleteOutline
                            style={{
                              fontSize: '16px',
                            }}
                          />
                          Remove User
                        </div>
                      </button>
                    </li>
                    <li
                      style={{
                        borderTop: '1px solid #E2E6EA',
                      }}
                    >
                      <button className={styles.list_button} onClick={() => handleEditOrg(org)}>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                          }}
                        >
                          <CiEdit
                            style={{
                              fontSize: '16px',
                            }}
                          />
                          Edit Organization
                        </div>
                      </button>
                    </li>
                    <li>
                      <button className={styles.list_button} onClick={() => handleDeleteOrg(org)}>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            color: '#D14D41',
                          }}
                        >
                          <MdOutlineDeleteOutline
                            style={{
                              fontSize: '16px',
                            }}
                          />
                          Delete Organization
                        </div>
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </td>
        </tr>
      );
    });
  };

  return (
    <>
      {/* Show Spinner while loading organizations or users */}
      {(isLoading || isLoadingUsers) && <Spinner />}

      {/* Display Organizations Table */}
      {allOrganizations && allUsers && !isLoading && !isLoadingUsers && !error && !errorUsers && (
        <>
          <div className={styles.usersTable}>
            <div className={styles.toolbar}>
              <div className={styles.searchWrap}>
                <FiSearch className={styles.searchIcon} />
                <input
                  type="text"
                  className={styles.search}
                  placeholder="Search name, email, phone, or address"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search organizations"
                />
                {search && (
                  <button
                    type="button"
                    className={styles.clearSearch}
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                  >
                    <FiX />
                  </button>
                )}
              </div>
              <span className={styles.resultCount}>
                {visibleOrgs.length} of {allOrganizations.length} organizations
              </span>
            </div>

            <div className={styles.tableContainer}>
              <table>
                <thead>
                  <tr>
                    <th>
                      <button
                        type="button"
                        className={styles.sortable}
                        onClick={() => handleSort('name')}
                      >
                        Name {sortIcon('name')}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={styles.sortable}
                        onClick={() => handleSort('email')}
                      >
                        Email {sortIcon('email')}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={styles.sortable}
                        onClick={() => handleSort('phone')}
                      >
                        Phone Number {sortIcon('phone')}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={styles.sortable}
                        onClick={() => handleSort('address')}
                      >
                        Address {sortIcon('address')}
                      </button>
                    </th>
                    <th>
                      <span>Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOrgs.length > 0 ? (
                    renderOrganizations()
                  ) : (
                    <tr>
                      <td colSpan={5} className={styles.emptyState}>
                        No organizations match &ldquo;{search}&rdquo;. Try a different name, email,
                        or address.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          <Pagination
            currentPage={currentPage}
            lastPage={Math.ceil(visibleOrgs.length / limitOfOrgs)}
            maxLength={7}
            setCurrentPage={setCurrentPage}
          />
        </>
      )}

      {/* Add User to Organization Modal */}
      {openAddUserModal && selectedOrg && (
        // @ts-ignore - Type mismatch between Modal's User interface and useUsers's User interface
        <Modal
          open={openAddUserModal}
          setOpen={setOpenAddUserModal}
          addUserToOrganization={true}
          orgId={selectedOrg.id.toString()}
          organization={selectedOrg.name}
          allUsers={allUsers}
        />
      )}

      {/* Remove User from Organization Modal */}
      {openRemoveUserModal && selectedOrg && (
        // @ts-ignore - Type mismatch between Modal's User interface and useUsers's User interface
        <Modal
          open={openRemoveUserModal}
          setOpen={setOpenRemoveUserModal}
          confirmOrganizationUser={true}
          orgId={selectedOrg.id.toString()}
          organization={selectedOrg.name}
          allUsers={allUsers}
        />
      )}

      {/* Edit Organization Modal */}
      {openEditOrgModal && selectedOrg && (
        // @ts-ignore - Type issues with Modal component
        <Modal
          open={openEditOrgModal}
          setOpen={setOpenEditOrgModal}
          editOrg={true}
          orgId={selectedOrg.id.toString()}
        />
      )}

      {/* Confirm Delete Organization Modal */}
      {openConfirmDeleteOrgModal && selectedOrg && (
        // @ts-ignore - Type issues with Modal component
        <Modal
          open={openConfirmDeleteOrgModal}
          setOpen={setOpenConfirmDeleteOrgModal}
          confirmOrganization={true}
          orgId={selectedOrg.id.toString()}
        />
      )}

      {/* Display Errors */}
      {error && <p>An error occurred: {error.message}</p>}
      {errorUsers && <p>An error occurred while fetching users: {errorUsers.message}</p>}
    </>
  );
}

export default ClientsManagement;